import React, { useEffect, useMemo, useRef, useState } from "react";

type XY = { x: number; y: number };
type Placed = { x: number; y: number; scale: number };

export type FirehoseLayout = {
    extinguisher?: Partial<Placed>;
    computer?: Partial<Placed>;
    /** Fine-tune impact point inside the computer (local px) */
    hit?: Partial<XY>;

    /** Binary stream tuning */
    bits?: {
        /** Offset loodrecht op de straal (negatief = “boven” de straal) in scene px */
        dy?: number;
        /** Extra verschuiving in x-richting in scene px */
        dx?: number;
        /** Visual size in CSS px (auto-compensated against SVG scaling) */
        sizePx?: number;
        /** “Dur” wordt hier als snelheid geïnterpreteerd als je wilt; anders negeren */
        dur?: string;
        /** Hoeveel tekens per seconde (default 18) */
        rate?: number;
        /** Snelheid over de curve in px/sec (default 360) */
        speed?: number;
        /** Max aantal actieve bits (default 90) */
        max?: number;
    };
};

export type FirehoseSceneProps = {
    className?: string;
    style?: React.CSSProperties;
    playing?: boolean;
    showBackground?: boolean;
    monitorFrameColor?: string;
    layout?: FirehoseLayout;
};

const EXTINGUISHER_PATH_D =
    "M 3.77,49.603 C 3.667061,49.5429 3.5821164,49.398254 3.5590464,49.244124 c -0.07065,-0.47201 -0.09767,-17.033675 -0.04198,-25.72826 0.05906,-9.220574 0.04128,-8.836117 0.468771,-10.141239 0.400546,-1.222841 1.085206,-2.211602 1.84684,-2.667135 0.414384,-0.247846 1.284203,-0.529848 2.065882,-0.669785 0.3159,-0.05656 0.600195,-0.1411863 0.631758,-0.1880763 0.03156,-0.04689 0.05792,-0.88344 0.05859,-1.858996 l 0.0012,-1.773744 h -0.827413 -0.827389 l -2.200502,2.187358 -2.200501,2.1873543 -0.176943,1.614884 c -0.09733,0.888183 -0.184722,1.621373 -0.194221,1.629311 -0.0095,0.008 -0.451044,0.04014 -0.98122,0.07155 l -0.96396496,0.05711 0.05073,-1.262996 c 0.105642,-2.629972 0.35317,-4.3440513 0.74734796,-5.1750573 0.260777,-0.549779 2.802705,-2.993879 4.918251,-4.728958 l 0.976401,-0.800797 1.230255,-0.0024 1.230255,-0.0024 V 1.1548017 0.31775468 l 0.8396976,-2.18e-4 c 0.461836,-1.09e-4 1.551487,-0.02375 2.421456,-0.05251 l 1.581758,-0.0523 v 1.03725702 1.037255 l 0.800719,0.05283 c 1.051944,0.06941 1.653251,0.169443 2.577606,0.428811 l 0.761591,0.213696 v 1.330923 1.330923 l -1.026632,0.2534 c -0.564646,0.139371 -1.350908,0.29213 -1.747256,0.339464 l -0.720635,0.08607 -0.0548,1.521238 c -0.03013,0.83668 -0.07166,1.59366 -0.0923,1.682178 -0.03318,0.142352 0.05091,0.187882 0.728019,0.394275 1.895019,0.5776163 2.933639,1.3937063 3.684705,2.8952073 0.736592,1.472567 0.527589,-0.133794 0.506145,17.951431 -0.186521,12.151066 0.0052,18.728286 -0.255813,18.835315 -5.116308,0 -0.423456,-0.0121 -5.116308,0 z M 17.885366,22.559239 V 18.812456 H 11.714561 5.5437444 v 3.746783 3.74678 h 6.1708166 6.170805 z";

const DEFAULTS = {
    extinguisher: { x: 210, y: 235, scale: 4.6 } as Placed,
    computer: { x: 520, y: 35, scale: 1.15 } as Placed,
    hit: { x: 130, y: 110 } as XY,
    waterOrigin: { x: 18.354, y: 4.313 } as XY,
    bits: {
        dy: -30,
        dx: 0,
        sizePx: 16,
        rate: 4,
        speed: 180,
        max: 40,
    },
};

type BitParticle = { id: number; text: string; born: number };
type BitDraw = { id: number; text: string; x: number; y: number; a: number; o: number };

const BINARY_STREAM = "10111010 ";

export default function FirehoseScene({
    className,
    style,
    playing = true,
    showBackground = false,
    monitorFrameColor = "rgba(100, 100, 100, 0.90)",
    layout,
}: FirehoseSceneProps) {
    const uid = useMemo(() => `fh_${Math.random().toString(16).slice(2)}`, []);
    const wrapRef = useRef<HTMLDivElement | null>(null);

    const L = useMemo(() => {
        const e = { ...DEFAULTS.extinguisher, ...(layout?.extinguisher ?? {}) };
        const c = { ...DEFAULTS.computer, ...(layout?.computer ?? {}) };
        const h = { ...DEFAULTS.hit, ...(layout?.hit ?? {}) };
        const bits = { ...DEFAULTS.bits, ...(layout?.bits ?? {}) };
        return { e, c, h, bits };
    }, [layout]);

    // bit font-size keep in px on screen
    const [bitFontSvg, setBitFontSvg] = useState<number>(16);
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => {
            const w = el.clientWidth || 1;
            const scale = w / 900;
            const desiredPx = Number.isFinite(L.bits.sizePx) ? (L.bits.sizePx as number) : 16;
            setBitFontSvg(desiredPx / Math.max(scale, 0.0001));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [L.bits.sizePx]);

    // Water start/target
    const waterStartX = L.e.x + DEFAULTS.waterOrigin.x * L.e.scale;
    const waterStartY = L.e.y + DEFAULTS.waterOrigin.y * L.e.scale;
    const targetX = L.c.x + L.h.x * L.c.scale;
    const targetY = L.c.y + L.h.y * L.c.scale;

    const waterD = makeWaterD(waterStartX, waterStartY, targetX, targetY);
    const impactD = makeImpactD(L.h.x, L.h.y);

    // Path ref for point sampling
    const pathRef = useRef<SVGPathElement | null>(null);
    const pathLenRef = useRef<number>(0);

    // particles state (we keep it small)
    const particlesRef = useRef<BitParticle[]>([]);
    const [drawBits, setDrawBits] = useState<BitDraw[]>([]);
    const nextId = useRef(1);
    const lastSpawn = useRef<number>(0);

    // Re-cache path length when path changes / mounts
    useEffect(() => {
        const p = pathRef.current;
        if (!p) return;
        try {
            pathLenRef.current = p.getTotalLength();
        } catch {
            pathLenRef.current = 0;
        }
    }, [waterD]);

    // Single RAF loop: spawn + update positions
    useEffect(() => {
        if (!playing) return;

        const rate = Math.max(1, Number(L.bits.rate ?? 5)); // chars/sec
        const spawnEveryMs = 1000 / rate;
        const speed = Math.max(40, Number(L.bits.speed ?? 150)); // px/sec
        const max = Math.max(10, Math.min(220, Number(L.bits.max ?? 60)));

        const dy = Number(L.bits.dy ?? -18);
        const dx = Number(L.bits.dx ?? 0);

        // reset timing zodat het direct weer begint als playing togglet
        lastSpawn.current = performance.now();

        let raf = 0;

        const tick = (now: number) => {
            const path = pathRef.current;
            const len = pathLenRef.current || 0;

            // (re)cache lengte als die nog 0 is (soms pas na mount beschikbaar)
            if (path && len === 0) {
                try {
                    pathLenRef.current = path.getTotalLength();
                } catch {
                    pathLenRef.current = 0;
                }
            }

            const totalLen = pathLenRef.current;

            // --- spawn (1 voor 1 uit de nozzle), met catch-up ---
            while (now - lastSpawn.current >= spawnEveryMs) {
                lastSpawn.current += spawnEveryMs;

                const idx = (nextId.current - 1) % BINARY_STREAM.length;
                const raw = BINARY_STREAM[idx];

                // spatie = pauze
                if (raw === " ") {
                    nextId.current++;
                    continue;
                }

                const text = raw; // 1 char per particle ("0" of "1")
                particlesRef.current.push({ id: nextId.current++, text, born: lastSpawn.current });

                if (particlesRef.current.length > max) {
                    particlesRef.current.splice(0, particlesRef.current.length - max);
                }
            }

            // --- update positions ---
            if (path && totalLen > 0) {
                const lifeSec = totalLen / speed; // tijd om van start tot eind te gaan
                const fadeOutSec = 0.35;

                const out: BitDraw[] = [];
                const keep: BitParticle[] = [];

                for (const b of particlesRef.current) {
                    const t = (now - b.born) / 1000;
                    if (t > lifeSec + 0.15) continue; // klaar, weggooien

                    const dist = Math.min(totalLen, t * speed);
                    const p1 = path.getPointAtLength(dist);
                    const p2 = path.getPointAtLength(Math.min(totalLen, dist + 1.5));
                    const ang = Math.atan2(p2.y - p1.y, p2.x - p1.x);

                    const nx = -Math.sin(ang);
                    const ny = Math.cos(ang);

                    const x = p1.x + nx * dy + dx;
                    const y = p1.y + ny * dy;

                    const fadeStart = Math.max(0, lifeSec - fadeOutSec);
                    const o = t < fadeStart ? 1 : Math.max(0, 1 - (t - fadeStart) / fadeOutSec);

                    out.push({ id: b.id, text: b.text, x, y, a: ang, o });
                    keep.push(b);
                }

                particlesRef.current = keep;
                setDrawBits(out);
            }

            raf = requestAnimationFrame(tick);
        };

        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [playing, waterD, L.bits.rate, L.bits.speed, L.bits.max, L.bits.dy, L.bits.dx]);

    return (
        <div ref={wrapRef} className={className} style={{ width: "100%", height: "100%", ...style }}>
            <style>{css(uid)}</style>

            <svg viewBox="0 0 900 420" preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%", display: "block" }}>
                <defs>
                    <linearGradient id={`${uid}_water`} x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0" stopColor="rgba(120, 210, 255, 0.0)" />
                        <stop offset="0.15" stopColor="rgba(120, 210, 255, 0.65)" />
                        <stop offset="0.55" stopColor="rgba(120, 210, 255, 0.9)" />
                        <stop offset="1" stopColor="rgba(255, 255, 255, 0.0)" />
                    </linearGradient>

                    <radialGradient id={`${uid}_flame`} cx="50%" cy="60%" r="70%">
                        <stop offset="0" stopColor="#fff4a6" />
                        <stop offset="0.45" stopColor="#ff9b2f" />
                        <stop offset="1" stopColor="#d81b1b" />
                    </radialGradient>

                    <filter id={`${uid}_glow`} x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="3" result="b" />
                        <feColorMatrix
                            in="b"
                            type="matrix"
                            values="
                1 0 0 0 0
                0 0.7 0 0 0
                0 0 0.1 0 0
                0 0 0 0.9 0"
                            result="c"
                        />
                        <feMerge>
                            <feMergeNode in="c" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>

                    <filter id={`${uid}_waterBlur`} x="-30%" y="-30%" width="160%" height="160%">
                        <feGaussianBlur stdDeviation="1.2" />
                    </filter>
                </defs>

                {showBackground && <rect x="0" y="0" width="900" height="420" rx="24" className={`${uid}_bg`} />}

                {/* Extinguisher */}
                <g transform={`translate(${L.e.x}, ${L.e.y}) scale(${L.e.scale})`}>
                    <path d={EXTINGUISHER_PATH_D} fill="#aa0000" paintOrder="fill" />
                </g>

                {/* Computer background (grijs achter), screen boven frame */}
                <g transform={`translate(${L.c.x}, ${L.c.y}) scale(${L.c.scale})`}>
                    <rect x="0" y="40" width="260" height="180" rx="18" fill={monitorFrameColor} stroke="rgba(255,255,255,0.10)" strokeWidth={2} />
                    <rect x="115" y="220" width="30" height="30" rx="10" fill={monitorFrameColor} opacity={0.95} />
                    <rect x="70" y="248" width="120" height="18" rx="9" fill={monitorFrameColor} opacity={0.95} />
                    <rect x="18" y="58" width="224" height="128" rx="10" className={`${uid}_screen`} />
                </g>

                {/* Onzichtbare curve voor sampling */}
                <path ref={pathRef} d={waterD} fill="none" opacity={0} />

                {/* Flame boven het scherm */}
                <g transform={`translate(${L.c.x}, ${L.c.y}) scale(${L.c.scale})`} filter={`url(#${uid}_glow)`}>
                    <path
                        d="M 130 54
               C 112 74, 112 92, 130 108
               C 105 98, 96 132, 130 150
               C 164 132, 155 98, 130 108
               C 148 92, 148 74, 130 54 Z"
                        fill={`url(#${uid}_flame)`}
                        className={`${uid}_flame ${playing ? `${uid}_flicker1` : ""}`}
                    />
                    <path
                        d="M 130 72
               C 120 85, 121 98, 130 110
               C 117 105, 112 124, 130 134
               C 148 124, 143 105, 130 110
               C 139 98, 140 85, 130 72 Z"
                        fill="#fff2b5"
                        opacity="0.85"
                        className={`${uid}_flameInner ${playing ? `${uid}_flicker2` : ""}`}
                    />
                </g>

                {/* Water boven het scherm */}
                <g opacity={playing ? 1 : 0}>
                    <path d={waterD} className={`${uid}_waterPath ${playing ? `${uid}_waterAnim` : ""}`} fill="none" filter={`url(#${uid}_waterBlur)`} />
                    <path d={waterD} className={`${uid}_waterCore ${playing ? `${uid}_waterAnim2` : ""}`} fill="none" />
                </g>

                {/* Bits: echt “1 voor 1” uit de nozzle */}
                <g opacity={playing ? 1 : 0}>
                    {drawBits.map((b) => {
                        const deg = (b.a * 180) / Math.PI;
                        return (
                            <text
                                key={b.id}
                                className={`${uid}_bits`}
                                x={b.x}
                                y={b.y}
                                opacity={b.o}
                                fontSize={bitFontSvg}
                                transform={`rotate(${deg} ${b.x} ${b.y})`}
                            >
                                {b.text}
                            </text>
                        );
                    })}
                </g>

                {/* Impact + screen glow (boven het scherm) */}
                <g transform={`translate(${L.c.x}, ${L.c.y}) scale(${L.c.scale})`}>
                    <g opacity={playing ? 1 : 0}>
                        <path d={impactD} className={`${uid}_splash ${playing ? `${uid}_splashAnim` : ""}`} fill="none" />
                    </g>
                    <rect x="18" y="58" width="224" height="128" rx="10" className={`${uid}_screenGlow`} />
                </g>
            </svg>
        </div>
    );
}

function makeWaterD(x0: number, y0: number, xT: number, yT: number) {
    const cx1 = x0 + (xT - x0) * 0.35;
    const cy1 = y0 - 55;
    const cx2 = x0 + (xT - x0) * 0.75;
    const cy2 = yT - 25;
    return `M ${x0} ${y0} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${xT} ${yT}`;
}

function makeImpactD(cx: number, cy: number) {
    const r = 26;
    return `M ${cx - r} ${cy}
          C ${cx - r * 0.45} ${cy - r * 0.65}, ${cx + r * 0.45} ${cy - r * 0.65}, ${cx + r} ${cy}
          C ${cx + r * 0.45} ${cy + r * 0.65}, ${cx - r * 0.45} ${cy + r * 0.65}, ${cx - r} ${cy}`;
}

function css(uid: string) {
    return `
  .${uid}_bg { fill: rgba(10, 12, 18, 0.92); }

  .${uid}_waterPath {
    stroke: url(#${uid}_water);
    stroke-width: 18;
    stroke-linecap: round;
    stroke-linejoin: round;
    opacity: 0.9;
    stroke-dasharray: 14 10;
  }
  .${uid}_waterCore {
    stroke: rgba(220, 250, 255, 0.78);
    stroke-width: 6;
    stroke-linecap: round;
    opacity: 0.9;
    stroke-dasharray: 8 12;
  }
  .${uid}_waterAnim { animation: ${uid}_dash 0.55s linear infinite; }
  .${uid}_waterAnim2 { animation: ${uid}_dash2 0.42s linear infinite; }
  @keyframes ${uid}_dash { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -48; } }
  @keyframes ${uid}_dash2 { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -60; } }

  .${uid}_screen { fill: rgba(40, 60, 110, 0.72); stroke: rgba(255,255,255,0.12); stroke-width: 2; }
  .${uid}_screenGlow { fill: none; stroke: rgba(120, 210, 255, 0.22); stroke-width: 6; opacity: 0.7; }

  .${uid}_splash {
    stroke: rgba(200, 245, 255, 0.55);
    stroke-width: 4;
    stroke-linecap: round;
    stroke-dasharray: 10 18;
    opacity: 0.0;
  }
  .${uid}_splashAnim { animation: ${uid}_splash 1.1s ease-in-out infinite; }
  @keyframes ${uid}_splash {
    0%   { stroke-dashoffset: 0; opacity: 0.0; }
    18%  { opacity: 0.75; }
    55%  { opacity: 0.55; }
    100% { stroke-dashoffset: -70; opacity: 0.0; }
  }

  .${uid}_flame { transform-origin: 130px 120px; }
  .${uid}_flameInner { transform-origin: 130px 118px; }
  .${uid}_flicker1 { animation: ${uid}_flicker1 0.18s ease-in-out infinite alternate; }
  .${uid}_flicker2 { animation: ${uid}_flicker2 0.22s ease-in-out infinite alternate; }
  @keyframes ${uid}_flicker1 {
    0%   { transform: translate(-1px, 0px) scale(1.02) rotate(-1deg); opacity: 0.95; }
    100% { transform: translate(1px, -2px) scale(0.98) rotate(1deg); opacity: 1; }
  }
  @keyframes ${uid}_flicker2 {
    0%   { transform: translate(1px, 0px) scale(1.01) rotate(1deg); opacity: 0.75; }
    100% { transform: translate(-1px, -1px) scale(0.97) rotate(-1deg); opacity: 0.9; }
  }

  .${uid}_bits {
    fill: rgb(168 144 39);
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    letter-spacing: 2px;
    dominant-baseline: middle;
    paint-order: stroke;
    stroke: rgba(10, 12, 18, 0.35);
    stroke-width: 2px;
    pointer-events: none;
  }
  `;
}
